import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/forms/Buttons/Button";
import EditTeamForm from "./EditTeamForm";
import { postApproveTeam } from "../../../services/olympiad/teamService";
import { toast } from "react-toastify";

export default function TeamHeader({ team, onTeamUpdate, isSupervisor }) {
  const [t] = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  const supervisor = team.team_members?.find(
    (member) => member.role === "SUPERVISOR"
  );

  const totalResults_for_first_level = team.team_members?.reduce(
    (prev, member) => {
      return (
        prev +
        (member.user?.user_results?.find((result) => result.variant.level === 0)
          ?.result || 0)
      );
    },
    0
  );
  const isFirstLevelPassed = team?.team_passed?.find(
    (passed) => passed.level === 0
  )?.is_passed;

  const isPublishedFirstLevel = team.team_members?.some(
    (member) =>
      member?.user?.user_results?.find((result) => result?.variant?.level === 0)
        ?.is_published
  );

  const handleEditSuccess = (updatedTeam) => {
    onTeamUpdate(updatedTeam);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const getErrorMessage = (errorCode) => {
    const errorMap = {
      1: t("team.error_permission_denied_approve"),
      2: t("team.error_team_not_in_draft_status"),
      3: t("team.error_team_not_reached_minimum_participant_limit"),
      4: t("team.error_team_exceeded_maximum_supervisor_limit"),
      5: t("team.error_team_not_reached_minimum_supervisor_limit"),
      6: t("team.error_invalid_status_transition"),
      7: t("team.error_team_already_approved"),
      8: t("team.error_team_exceeded_maximum_participant_limit"),
      9: t("team.error_region_full"),
    };
    return errorMap[errorCode] || t("team.error_approving_team");
  };

  const handleApproveTeam = () => {
    if (toast.isActive(`${team.uuid}-approve`)) return;
    toast(
      <div className="flex flex-col gap-2 pt-2">
        <p>{t("team.send_application_confirmation_question")}</p>
        <p>{t("team.send_application_confirmation_warning")}</p>
        <div className="flex justify-end gap-2">
          <Button
            text={t("buttons.send_application")}
            color="primary"
            onClick={() => {
              toast.dismiss(team.uuid);
              postApproveTeam(team.uuid)
                .then((res) => {
                  onTeamUpdate(res);
                  toast.success(t("team.approved"));
                })
                .catch((err) => {
                  console.error("Error approving team:", err);
                  const errorCode = err.response?.data?.error_code;
                  const errorMessage = errorCode
                    ? getErrorMessage(errorCode)
                    : err.response?.data?.detail ||
                      t("team.error_approving_team");
                  toast.error(errorMessage);
                })
                .finally(() => {
                  toast.dismiss(`${team.uuid}-approve`);
                });
            }}
          />
          <Button
            text={t("buttons.cancel")}
            color="secondary"
            onClick={() => toast.dismiss(`${team.uuid}-approve`)}
          />
        </div>
      </div>,
      { hideProgressBar: true, toastId: `${team.uuid}-approve` }
    );
  };
  if (isEditing) {
    return (
      <div className="pb-6">
        <EditTeamForm
          team={team}
          onCancel={handleCancel}
          onSuccess={handleEditSuccess}
        />
      </div>
    );
  }

  return (
    <div className="flex lg:flex-row gap-4 lg:gap-0 flex-col items-center justify-between mb-6">
      <div className="flex-1">
        <h2 className="text-xl font-semibold mb-2">{team.name}</h2>
        <div className="text-sm text-gray-600 space-y-1">
          <p>
            <span className="font-medium">{t("team.supervisor")}:</span>{" "}
            {supervisor?.user?.first_name} {supervisor?.user?.last_name}{" "}
            {supervisor?.user?.middle_name}
          </p>
          <p>
            <span className="font-medium">{t("team.region")}:</span>{" "}
            {team.region?.name}
          </p>
          {!team.region?.is_republican_city && (
            <>
              <p>
                <span className="font-medium">{t("team.district")}:</span>{" "}
                {team.district}
              </p>
              <p>
                <span className="font-medium">{t("team.city")}:</span>{" "}
                {team.city}
              </p>
            </>
          )}
          <p>
            <span className="font-medium">{t("team.school")}:</span>{" "}
            {team.school}
          </p>
          {team.exam_center && (
            <>
              <p>
                <span className="font-medium">{t("team.exam_center")}:</span>{" "}
                {team.exam_center?.name}
              </p>
              <p>
                <span className="font-medium">
                  {t("team.exam_center_address")}:
                </span>{" "}
                {team.exam_center?.name}
              </p>
              <p>
                <span className="font-medium">
                  {t("team.exam_center_2gis")}:
                </span>{" "}
                <a
                  href={team.exam_center?.geo_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  {team.exam_center?.geo_link}
                </a>
              </p>
            </>
          )}
          <p>
            <span className="font-medium">{t("team.status")}:</span>{" "}
            <span
              className={`${
                team.status === "APPROVED"
                  ? "text-green-500 font-bold"
                  : team.status === "DRAFT"
                  ? "text-gray-500"
                  : "text-secondary"
              }`}
            >
              {team.status === "DRAFT"
                ? t("team.draft")
                : team.status === "APPROVED"
                ? "Тобыңыз сәтті тіркелді"
                : t("team.draft")}
            </span>
          </p>
          {isPublishedFirstLevel && (
            <>
              <p>
                <span className="font-medium">{t("team.status")}:</span>{" "}
                <span
                  className={`${
                    isFirstLevelPassed
                      ? "text-green-500 font-bold"
                      : "text-red-500"
                  }`}
                >
                  {team?.team_passed?.find((passed) => passed.level === 0)
                    ?.is_passed
                    ? t("admin.qualified")
                    : t("admin.unqualified")}
                </span>
              </p>
              <p className="font-bold text-primary">
                <span>{t("team.score")}:</span>{" "}
                <span>{totalResults_for_first_level}</span>
              </p>
            </>
          )}
        </div>
      </div>
      {team.status !== "APPROVED" && isSupervisor && (
        <div className="flex flex-col gap-2">
          <Button
            text={t("buttons.edit_team_info")}
            color="primary"
            onClick={() => setIsEditing(true)}
          />
          {/* {team.status === "DRAFT" && (
            <Button
              title={t("team.send_application_tooltip")}
              text={t("buttons.send_application")}
              color="primary"
              onClick={() => handleApproveTeam()}
            />
          )} */}
        </div>
      )}
    </div>
  );
}
